import { useState, useEffect } from 'react';
import { getPricingTier } from '../utils/pricingUtils';

// ipinfo.io token from existing implementation
const IPINFO_TOKEN = 'b259d22dc84b2e';

/**
 * Hook to detect user's region via ipinfo.io
 *
 * Replicates the existing logic from PaymentSelection.js but as a reusable hook.
 * Can be overridden via parameter for testing purposes.
 *
 * @param {string|null} override - Optional country code to use instead of detecting
 * @returns {Object} { region, pricingTier, loading, error }
 *   - region: Two-letter country code (e.g., "US", "PH")
 *   - pricingTier: Pricing tier key (e.g., "US", "PH", "TH", "TW", "JP")
 *   - loading: boolean
 *   - error: Error object or null
 */
export function useRegion(override = null) {
  const [region, setRegion] = useState(override || 'US');
  const [loading, setLoading] = useState(!override);
  const [error, setError] = useState(null);

  useEffect(() => {
    // If override is provided, use it directly
    if (override) {
      setRegion(override);
      setLoading(false);
      return;
    }

    const fetchRegion = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://ipinfo.io/json?token=${IPINFO_TOKEN}`
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch region: ${response.status}`);
        }

        const data = await response.json();
        const countryCode = data.country?.toUpperCase() || 'US';
        setRegion(countryCode);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch region:', err);
        setError(err);
        // Default to US on error
        setRegion('US');
      } finally {
        setLoading(false);
      }
    };

    fetchRegion();
  }, [override]);

  // Update region if override changes
  useEffect(() => {
    if (override) {
      setRegion(override);
      setLoading(false);
    }
  }, [override]);

  return {
    region,
    pricingTier: getPricingTier(region),
    loading,
    error
  };
}

/**
 * Get display name for a region
 * @param {string} countryCode - Two-letter country code
 * @returns {string} Human-readable region name
 */
export function getRegionDisplayName(countryCode) {
  const regionNames = {
    US: 'United States',
    PH: 'Philippines',
    MY: 'Malaysia',
    CN: 'China',
    AR: 'Argentina',
    BR: 'Brazil',
    IE: 'Ireland',
    GB: 'United Kingdom',
    FR: 'France',
    JP: 'Japan',
    KR: 'South Korea',
    IL: 'Israel',
    IT: 'Italy',
    TW: 'Taiwan',
    ES: 'Spain',
    PT: 'Portugal',
    PL: 'Poland',
    TH: 'Thailand',
    VN: 'Vietnam',
    ID: 'Indonesia',
    IN: 'India',
    NG: 'Nigeria'
  };

  return regionNames[countryCode] || countryCode;
}

export default useRegion;
